import { css } from '@emotion/react'
import { HFlow, Text, VFlow } from 'bold-ui'
import { SimNaoEnum, TextField } from 'components/form'
import { RecordStringSelectField } from 'components/form/core/RecordStringSelectField/RecordStringSelectField'
import { PesoAutorreferidoRadioGroupField } from 'components/form/field/SobrePesoRadioField'
import theme from 'config/theme'
import { differenceInYears } from 'date-fns'
import { SexoEnum } from 'graphql/types.generated'
import { MetaPath } from 'util/metaPath'
import { createFormSectionStyles } from 'view/cidadao/utils-cidadao'

import { FormRowSimNao } from '../../../../components/form/field/FormRowSimNao'
import {
  CondicoesSaudeAutorreferidasFormModel,
  doencaCardiacaAutorreferidoRecord,
  doencaRespiratoriaAutorreferidoRecord,
  doencaRinsAutorreferidoRecord,
} from '../types/model-cidadao'
import { FormSectionAccordionWithErrors } from './FormSectionAccordionWithErrors'

interface CondicoesSaudeAutorreferidasFormSectionProps {
  meta: MetaPath<CondicoesSaudeAutorreferidasFormModel>
  values: CondicoesSaudeAutorreferidasFormModel
  sexo: SexoEnum
  dataNascimento: string
}

export const CondicoesSaudeAutorreferidasFormSection = ({
  meta,
  values,
  sexo,
  dataNascimento,
}: CondicoesSaudeAutorreferidasFormSectionProps) => {
  const styles = createFormSectionStyles(theme)

  const idade = dataNascimento ? differenceInYears(new Date(), new Date(dataNascimento)) : 0

  return (
    <FormSectionAccordionWithErrors
      name={meta.openedAccordion}
      title='Condições de saúde autorreferidas'
      uuid='condicoessdesaudeautorreferidas'
      textStyle={css`
        font-size: 1rem;
      `}
      panelStyle={styles.rowsContainer}
    >
      {sexo !== SexoEnum.MASCULINO && (
        <FormRowSimNao name={meta.stGestante} title='Está gestante?' clearable disabled={idade < 9 || idade >= 60}>
          <TextField
            name={meta.maternidadeReferencia}
            label='Maternidade de referência'
            title='Maternidade de referência'
            disabled={values?.stGestante !== SimNaoEnum.SIM}
            style={styles.fixedWidthInputContainer}
          />
        </FormRowSimNao>
      )}

      <HFlow>
        <Text fontWeight='bold'>Sobre seu peso, você se considera?</Text>
        <PesoAutorreferidoRadioGroupField name={meta.autodenominacaoPeso} globalClearable />
      </HFlow>

      <FormRowSimNao name={meta.stAcamado} title='Está acamado?' clearable />
      <FormRowSimNao name={meta.stDomiciliar} title='Está domiciliado?' clearable />
      <FormRowSimNao
        name={meta.stPraticasComplementares}
        title='Usa outras práticas integrativas e complementares?'
        clearable
      />
      <FormRowSimNao name={meta.stFumante} title='Está fumante?' clearable />
      <FormRowSimNao name={meta.stUsoAlcool} title='Faz uso de álcool?' clearable />
      <FormRowSimNao name={meta.stUsoOutrasDrogas} title='Faz uso de outras drogas?' clearable />
      <FormRowSimNao
        name={meta.stProblemaSaudeMental}
        title='Teve diagnóstico de algum problema de saúde mental por profissional de saúde?'
        clearable
      />
      <FormRowSimNao name={meta.stHipertensaoArterial} title='Tem hipertensão arterial?' clearable />
      <FormRowSimNao name={meta.stDiabetes} title='Tem diabetes?' clearable />
      <FormRowSimNao name={meta.stCancer} title='Tem ou teve câncer?' clearable />
      <FormRowSimNao name={meta.stDerrame} title='Teve AVC/derrame?' clearable />
      <FormRowSimNao name={meta.stInfarto} title='Teve infarto?' clearable />
      <FormRowSimNao name={meta.stHanseniase} title='Está com hanseníase?' clearable />
      <FormRowSimNao name={meta.stTuberculose} title='Está com tuberculose?' clearable />

      <FormRowSimNao name={meta.stDoencaCardiaca} title='Teve doença cardíaca/do coração?' clearable>
        <div css={styles.fixedWidthInputContainer}>
          <RecordStringSelectField
            name={meta.doencaCardiaca}
            record={doencaCardiacaAutorreferidoRecord}
            label='Indique qual(is)'
            multiple
            disabled={values?.stDoencaCardiaca !== SimNaoEnum.SIM}
            required={values?.stDoencaCardiaca === SimNaoEnum.SIM}
          />
        </div>
      </FormRowSimNao>

      <FormRowSimNao name={meta.stDoencaRespiratoria} title='Tem doença respiratória/no pulmão?' clearable>
        <div css={styles.fixedWidthInputContainer}>
          <RecordStringSelectField
            name={meta.doencasRespiratorias}
            record={doencaRespiratoriaAutorreferidoRecord}
            label='Indique qual(is)'
            multiple
            disabled={values?.stDoencaRespiratoria !== SimNaoEnum.SIM}
            required={values?.stDoencaRespiratoria === SimNaoEnum.SIM}
          />
        </div>
      </FormRowSimNao>

      <FormRowSimNao name={meta.stProblemaRins} title='Tem ou teve problemas nos rins?' clearable>
        <div css={styles.fixedWidthInputContainer}>
          <RecordStringSelectField
            name={meta.doencaRins}
            record={doencaRinsAutorreferidoRecord}
            label='Indique qual(is)'
            multiple
            disabled={values?.stProblemaRins !== SimNaoEnum.SIM}
            required={values?.stProblemaRins === SimNaoEnum.SIM}
          />
        </div>
      </FormRowSimNao>

      <FormRowSimNao
        name={meta.stInternacaoUltimos12Meses}
        title='Teve alguma internação nos últimos 12 meses?'
        clearable
      >
        <TextField
          name={meta.causaInternacaoUltimos12Meses}
          label='Por qual causa?'
          disabled={values?.stInternacaoUltimos12Meses !== SimNaoEnum.SIM}
          style={styles.fixedWidthInputContainer}
        />
      </FormRowSimNao>

      <FormRowSimNao name={meta.stPlantasMedicinais} title='Usa plantas medicinais?' clearable>
        <TextField
          name={meta.plantasMedicinais}
          label='Indique qual(is)'
          disabled={values?.stPlantasMedicinais !== SimNaoEnum.SIM}
          style={styles.fixedWidthInputContainer}
        />
      </FormRowSimNao>

      <HFlow>
        <Text fontWeight='bold'>Possui alguma outra condição de saúde?</Text>
        <VFlow style={styles.fixedWidthInputContainer}>
          <TextField name={meta.condicaoSaude1} label='Indique qual(is)' />
          <TextField name={meta.condicaoSaude2} label='Indique qual(is)' />
          <TextField name={meta.condicaoSaude3} label='Indique qual(is)' />
        </VFlow>
      </HFlow>
    </FormSectionAccordionWithErrors>
  )
}
